import { styled } from "@linaria/react";
import { ImageBrokenIcon } from "@phosphor-icons/react";

import { withTheme } from "@gx/dls/theme";
import type { PropsWithTheme } from "@gx/dls/theme/types";
import { SourceSyncResource } from "@gx/ts-contracts/services/source/resources/source_sync_pb";

import EmptyLayout from "@/layouts/EmptyLayout";
import ErrorLayout from "@/layouts/ErrorLayout";
import LoadingLayout from "@/layouts/LoadingLayout";

import { BrowserbaseReplayPlayer } from "@/pages/sources/components/edit/tabs/syncs/browserbase/BrowserbaseReplayViewer";
import { BrowserbaseStatusChip } from "@/pages/sources/components/edit/tabs/syncs/browserbase/BrowserbaseStatusChip";
import {
  useBrowserbaseLiveviewIframeDisconnect,
  useBrowserbaseRecording,
} from "@/pages/sources/components/edit/tabs/syncs/browserbase/hooks";
import { extractSyncMetadata } from "@/pages/sources/components/edit/tabs/syncs/browserbase/utils";

const EditSourceSyncsBrowserbaseWrapper = withTheme(styled.div<PropsWithTheme>`
  position: relative;
  width: 100%;
  height: 420px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.background.tertiary};
`);

const BrowserbaseLiveviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  pointer-events: none;
`;

export function EditSourceSyncsBrowserbase({ sync }: { sync: SourceSyncResource }) {
  const { liveViewLink, sessionRecordingUri, isLive, isTerminal } = extractSyncMetadata(sync);
  const { events, isLoading, isError, isEmpty } = useBrowserbaseRecording(sessionRecordingUri);
  const { hasDisconnected, iframeRef } = useBrowserbaseLiveviewIframeDisconnect(
    liveViewLink,
    sync.id,
  );

  if (isLive && liveViewLink && !hasDisconnected) {
    return (
      <EditSourceSyncsBrowserbaseWrapper>
        <BrowserbaseLiveviewIframe
          ref={iframeRef}
          src={liveViewLink}
          title="Live session view"
          allow="clipboard-read; clipboard-write"
          sandbox="allow-same-origin allow-scripts"
        />
        <BrowserbaseStatusChip variant="live" />
      </EditSourceSyncsBrowserbaseWrapper>
    );
  }

  if (events && events.length > 0) {
    return (
      <EditSourceSyncsBrowserbaseWrapper>
        <BrowserbaseReplayPlayer events={events} />
      </EditSourceSyncsBrowserbaseWrapper>
    );
  }

  if (isError) {
    return (
      <EditSourceSyncsBrowserbaseWrapper>
        <ErrorLayout
          icon={ImageBrokenIcon}
          header="Couldn't load replay"
          message="Please try again later"
        />
      </EditSourceSyncsBrowserbaseWrapper>
    );
  }

  if (isEmpty || (isTerminal && !sessionRecordingUri)) {
    return (
      <EditSourceSyncsBrowserbaseWrapper>
        <EmptyLayout
          title="No recording available"
          description="There's no recording for this session"
        />
      </EditSourceSyncsBrowserbaseWrapper>
    );
  }

  if (isLoading || hasDisconnected) {
    return (
      <EditSourceSyncsBrowserbaseWrapper>
        <LoadingLayout
          title="Preparing replay"
          description="The recording will appear here shortly"
        />
      </EditSourceSyncsBrowserbaseWrapper>
    );
  }

  return (
    <EditSourceSyncsBrowserbaseWrapper>
      <LoadingLayout
        title="Starting up"
        description="The live view will appear here when your session is ready"
      />
    </EditSourceSyncsBrowserbaseWrapper>
  );
}
